import { ConfigProvider, DatePicker, Form, Input, message, Modal, Select, Button } from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { TollAreaModel } from "@/core/models/toll-area/toll-area.model";
import container from "@/lib/di/container";
import { TollAreaRepo } from "@/core/repos/tables/toll-area.repo";
import React from "react";
import { ReadMeterPeriodModel } from "@/core/models/read-meter-period.model";
import { ReadMeterPeriodRepo } from "@/core/repos/tables/read-meter-period.repo";
import { dayjsPipe } from "@/core/pipes/dayjs.pipe";
import { District } from "@/core/models/address/district.model";
import { Province } from "@/core/models/address/province.model";
import { Ward } from "@/core/models/address/ward.model";
import { AddressRepo } from "@/core/repos/address.repo";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import { usePermissionStore } from "@/core/stores/permission.store";
import { SaveButton } from "@/app/home/components/table/SaveButton";
import { SyncOutlined } from '@ant-design/icons';
import { useDrafts } from "@/core/stores/drafts.store";
import { ResetDraftButton } from '@/app/home/components/drafts/ResetDraftButton';
import { DEF_DATE_FORMAT } from "@/app/home/utils/global.utils";


const tollAreaRepo = container.get(TollAreaRepo);
const readMeterPeriodRepo = container.get(ReadMeterPeriodRepo);
const addressRepo = container.get(AddressRepo);

interface DetailModalProps {
    isModalOpen: boolean,
    setIsModalOpen: Dispatch<SetStateAction<boolean>>,
    data: ReadMeterPeriodModel,
}

const INPUT_WIDTH = 600;

export const DetailModal: React.FC<DetailModalProps> = ({isModalOpen, setIsModalOpen, data}) => {
    const permissionStore = usePermissionStore();
    const [form] = Form.useForm<ReadMeterPeriodModel>();
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [, setTollAreas] = useState<TollAreaModel[]>([]);
    const [messageApi, contextHolder] = message.useMessage();    
    const [provinces, setProvinces] = useState<Province[]>([]);
    const [districts, setDistricts] = useState<District[]>([]);
    const [wards, setWards] = useState<Ward[]>([]);

    // Draft logic for ReadMeterPeriodModel
    const draft = useDrafts((state) => state.getDraft('ReadMeterPeriodModel'));
    const _saveDraft = useDrafts((state) => state.saveDraft);
    const saveDraft = () => _saveDraft('ReadMeterPeriodModel', form.getFieldsValue());
    const _clearDraft = useDrafts((state) => state.clearDraft);
    const clearDraft = () => {
        _clearDraft('ReadMeterPeriodModel');
        form.setFieldsValue(data);
    };

    // Attach hooks to handle data changes
    const handleDataChange = async () => {
        await handleProvinceChange(data.provinceId??"");
        await handleDistrictChange(data.districtId??"");
        form.setFieldsValue(data);
        if (data.id && data.id.length > 0) {
            setIsEdit(true);
        } else {
            if (draft) form.setFieldsValue(draft);
            setIsEdit(false);
        }
    }
    useEffect(() => {
        handleDataChange();
    }, [data])

    //Initialize select options data
    const populateProvinces = async () => {
        const _provinces = (await addressRepo.getAllProvinces()).map(data => data[1]);
        setProvinces(_provinces);
    }

    // Initialize select options data
    const populateTollAreas = async () => {
        const _tollAreas = await tollAreaRepo.getAll();
        setTollAreas(_tollAreas);
    }
    const init = async () => {
        await populateProvinces();
        await populateTollAreas();
    }
    // componentDidMount equivalent, run exclusively on client-side
    useEffect(() => {init()}, []);

    const handleProvinceChange = async (provinceId: string) => {
        const _districts = (await addressRepo.getAllDistricts(provinceId)).map(data => data[1]);
        setDistricts(_districts);
        setWards([]);
        form.setFieldValue("districtId", "");
        form.setFieldValue("wardId", "");
    }

    const handleDistrictChange = async (districtId: string) => {
        const _wards = (await addressRepo.getAllWards(districtId)).map(data => data[1]);
        setWards(_wards);
        form.setFieldValue("wardId", "");
    }

    const handleSave = async () => {
        try {
            setLoading(true);
            const formData = await form.validateFields();
            if (isEdit) {
                formData.id = data.id;
                const result = await readMeterPeriodRepo.update({...data, ...formData});
                setIsModalOpen(false);
                if (result) {
                    messageApi.success({
                        content: "Đã cập nhật kỳ đọc chỉ số"
                    })
                } else {
                    messageApi.error({
                        content: "Cập nhật kỳ đọc chỉ số thất bại"
                    })
                }
            } else {
                const result = await readMeterPeriodRepo.create({...data, ...formData});
                setIsModalOpen(false);
                clearDraft();
                if (result) {
                    messageApi.success({
                        content: "Thêm kỳ đọc chỉ số mới thành công"
                    })
                } else {
                    messageApi.error({
                        content: "Thêm kỳ đọc chỉ số mới thất bại"
                    })
                }
            }
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    };
    const handleCancel = () => {
      if (!isEdit) saveDraft();
      setIsModalOpen(false);
    };

    return (
        <ConfigProvider theme={MODAL_THEME}>
            {contextHolder}
           <Modal open={isModalOpen} onCancel={handleCancel} width={670} centered
                styles={{
                    mask: {
                        backdropFilter: 'blur(2px)',
                        backgroundColor: 'var(--primary-mask)'
                    }
                }}
                footer={[
                    <div key={1} className="w-full flex flex-row justify-end px-1.5">
                        <SaveButton isEdit={isEdit} loading={loading} onClick={handleSave}/>
                    </div>,
                ]}
            >
                <div className="flex flex-row w-full items-center justify-start select-none">
                    <p className="ml-2 text-2xl font-bold text-primary">CHI TIẾT KỲ ĐỌC CHỈ SỐ</p>
                    {!isEdit && <ResetDraftButton onClick={clearDraft} />}
                </div>
                <div className="flex flex-row justify-center mt-6">
                    <Form<ReadMeterPeriodModel>
                        name="basic"
                        className="flex flex-col items-end"
                        form={form}
                        layout="vertical"
                        disabled={isEdit&&!permissionStore.page.edit}
                    >
                        {
                            isEdit
                            ?<Form.Item<ReadMeterPeriodModel>
                                label="Khu vực thu phí"
                                name="tollArea"
                                getValueProps={(e) => ({value: e.name})}
                            >
                                <Input type="text" style={{width: INPUT_WIDTH}} disabled/>
                            </Form.Item>
                            :(
                                <div>
                                    <Form.Item<ReadMeterPeriodModel>
                                        label="Năm"
                                        name="year"
                                        rules={[{ required: true, message: 'Vui lòng nhập năm!' }]}
                                    >
                                        <Input placeholder="Nhập năm" style={{width: INPUT_WIDTH}}></Input>
                                    </Form.Item>
                                    <Form.Item<ReadMeterPeriodModel>
                                        label="Tháng"
                                        name="month"
                                        rules={[{ required: true, message: 'Vui lòng nhập tháng!' }]}
                                    >
                                        <Input placeholder="Nhập tháng" style={{width: INPUT_WIDTH}}></Input>
                                    </Form.Item>
                                    <Form.Item<ReadMeterPeriodModel>
                                        label="Tỉnh/ TP"
                                        name="provinceId"
                                        rules={[{ required: true, message: 'Bạn phải chọn Tỉnh/ TP!' }]}
                                    >
                                        <Select placeholder="Chọn" style={{width: INPUT_WIDTH}} onChange={handleProvinceChange}>
                                            {provinces.map(province => <Select.Option key={province.id} value={province.id}>{province.name}</Select.Option>)}
                                        </Select>
                                    </Form.Item>
                                    <Form.Item<ReadMeterPeriodModel>
                                        label="Quận/ Huyện"
                                        name="districtId"
                                    >
                                        <Select placeholder="Chọn" style={{width: INPUT_WIDTH}} onChange={handleDistrictChange}>
                                            {districts.map(district => <Select.Option key={district.id} value={district.id}>{district.name}</Select.Option>)}
                                        </Select>
                                    </Form.Item>
                                    <Form.Item<ReadMeterPeriodModel>
                                        label="Phường/ Xã"
                                        name="wardId"
                                    >
                                        <Select placeholder="Chọn" style={{width: INPUT_WIDTH}}>
                                            {wards.map(ward => <Select.Option key={ward.id} value={ward.id}>{ward.name}</Select.Option>)}
                                        </Select>
                                    </Form.Item>
                                </div>
                            )
                        }
                        
                        <Form.Item<ReadMeterPeriodModel>
                            label="Từ ngày"
                            name="fromDate"
                            getValueProps={(i) => ({value: dayjsPipe(i)})}
                        >
                            <DatePicker format={DEF_DATE_FORMAT}placeholder="Chọn ngày bắt đầu" style={{width: INPUT_WIDTH}}></DatePicker>
                        </Form.Item>
                        <Form.Item<ReadMeterPeriodModel>
                            label="Đến ngày"
                            name="toDate"
                            getValueProps={(i) => ({value: dayjsPipe(i)})}
                        >
                            <DatePicker format={DEF_DATE_FORMAT}placeholder="Chọn ngày kết thúc" style={{width: INPUT_WIDTH}}></DatePicker>
                        </Form.Item>
                        
                        <Form.Item<ReadMeterPeriodModel>
                            label="Cho phép đọc"
                            name="isAllowReading"
                        >
                            <Select placeholder="Chọn trạng thái cho phép đọc" style={{width: INPUT_WIDTH}}>
                                <Select.Option value={true}>Có</Select.Option>
                                <Select.Option value={false}>Không</Select.Option>
                            </Select>
                        </Form.Item>
                    </Form>
                </div>
                <div className="mb-5 mx-2 border-t border-dashed border-gray-300"></div>
            </Modal>
        </ConfigProvider>
    )
}